/* eslint-disable no-unused-vars */
import clsx from "clsx";
import {
  MAX_TICKET_DATE,
  MIN_TICKET_DATE,
  isValidISODateString,
} from "@/lib/businessRules/settlementDateRules";
import { returnHolidayMessage } from "@/store/holidayCalendarStoreACTIONS";
import { useHolidayCheck } from "../helper/useHolidayCheck";

// LQC-2254 - native date entry. The app has no date-picker convention outside the
// deprecated analytics folder, and a native input already yields the yyyy-MM-dd string
// every outbound payload in the app sends, so there is nothing to convert.
//
// LQC-2677 - min/max are load-bearing, not decoration. The year box accepts more than
// four digits, and "22029-01-03" is a legal HTML date that .NET cannot parse, so it was
// reaching the API and failing the whole request. The bounds make the browser mark it
// invalid; the ticket's own required-field check refuses to send it.
interface ITicketDateInput {
  value: string; // yyyy-MM-dd
  onChange: (value: string) => void;
  testId: string;
  // LQC-2985 - calendars to check the date against. A holiday is flagged, not blocked.
  holidayCalendars?: string[];
  // Off in grid cells, which have no room for a line under the input.
  showHolidayNote?: boolean;
}

const TicketDateInput = ({
  value,
  onChange,
  testId,
  holidayCalendars,
  showHolidayNote = true,
}: ITicketDateInput) => {
  // Empty is "not filled in yet", which the ticket reports as missing rather than invalid.
  const isInvalid = !!value && !isValidISODateString(value);
  const holidays = useHolidayCheck(isInvalid ? "" : value, holidayCalendars);
  const isHoliday = holidays.length > 0;
  const holidayMessage = returnHolidayMessage(holidays);
  return (
    <div
      data-testid={`${testId}Container`}
      id={`${testId}Container`}
      className="w-full min-w-0"
    >
      <input
        data-testid={testId}
        id={testId}
        type="date"
        value={value}
        min={MIN_TICKET_DATE}
        max={MAX_TICKET_DATE}
        onChange={(e) => onChange(e.target.value)}
        title={holidayMessage || undefined}
        data-holiday={isHoliday || undefined}
        className={clsx(
          "h-[28px] w-full rounded-md border bg-transparent px-2 text-xs outline-none dark:bg-gray-800 dark:text-gray-200",
          isInvalid
            ? "border-red-600 bg-red-500/10 text-red-700 dark:border-red-500 dark:text-red-400"
            : isHoliday
              ? "border-amber-500 bg-amber-500/10 text-amber-700 dark:border-amber-400 dark:text-amber-300"
              : "border-gray-300 dark:border-gray-600",
        )}
      />
      {isHoliday && showHolidayNote && (
        <div
          data-testid={`${testId}HolidayNote`}
          id={`${testId}HolidayNote`}
          className="px-1 pt-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300"
        >
          {holidayMessage}
        </div>
      )}
    </div>
  );
};

export default TicketDateInput;
